import { useEffect, useState } from 'react';
import { DeliveryApiResponse, RecommendationItem } from './models';

// From appsettings.json EPiServer:ContentPersonalization — identical across all environments.
export const DEFAULT_IDIO_ENVIRONMENT = 'usea01.idio.episerver.net';
export const DEFAULT_IDIO_CLIENT_NAME = 'churchpensiongr';

// The delivery API is JSONP-only, so each request registers a uniquely named
// global callback and injects a <script> tag instead of using fetch/XHR.
let callbackCounter = 0;

const getCookie = (name: string): string => {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
};

export interface ContentRecommendationsState {
    items: RecommendationItem[];
    loading: boolean;
    error: boolean;
}

/**
 * Loads recommendations from the Optimizely Content Recommendations (idio)
 * delivery API — the same API the ip.js mustache widget calls internally.
 *
 * The visitor is identified by the `iv` first-party cookie set by ip.js, so
 * the page must also run the standard tracking snippet (see useIdioTracker)
 * for recommendations to be personalized.
 *
 * @param deliveryApiKey per-delivery key (Content Recs portal: Engage > Deliveries,
 *                       same value as the block's Delivery API Key in the CMS)
 * @param count          number of recommendations to request (rpp)
 * @param environment    idio environment host, e.g. usea01.idio.episerver.net
 */
export const useContentRecommendations = (
    deliveryApiKey: string,
    count: number,
    environment: string = DEFAULT_IDIO_ENVIRONMENT,
): ContentRecommendationsState => {

    const [items, setItems] = useState<RecommendationItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!deliveryApiKey) {
            setLoading(false);
            setError(true);
            return undefined;
        }

        setLoading(true);
        setError(false);

        callbackCounter += 1;
        const callbackName = `__idioRecommendationsCallback${callbackCounter}`;
        let cancelled = false;

        (window as any)[callbackName] = (data: DeliveryApiResponse, statusCode?: number) => {
            if (cancelled) {
                return;
            }
            const succeeded = data && data.content && (statusCode === undefined || statusCode === 200);
            setItems(succeeded ? data.content : []);
            setError(!succeeded);
            setLoading(false);
        };

        const visitorId = getCookie('iv');
        const host = encodeURIComponent(window.location.host);
        const query = [
            `key=${encodeURIComponent(deliveryApiKey)}`,
            `rpp=${count}`,
            'include_topics=true',
            `session[]=${host}`,
            `callback=${callbackName}`,
        ].join('&');

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://api.${environment}/1.0/users/idio_visitor_id:${encodeURIComponent(visitorId)}/content?${query}`;
        script.onerror = () => {
            if (!cancelled) {
                setItems([]);
                setError(true);
                setLoading(false);
            }
        };
        document.body.appendChild(script);

        return () => {
            cancelled = true;
            // leave a noop behind in case the JSONP response arrives after unmount
            (window as any)[callbackName] = () => undefined;
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };
    }, [deliveryApiKey, count, environment]);

    return { items, loading, error };
};

/**
 * Injects the standard Content Recommendations tracking snippet (_iaq + ip.js)
 * when the host page does not already include it. On the MVC redesign pages the
 * Personalization package injects this automatically via RequiredClientResources;
 * React pages rendered outside that layout need this hook once, near the app root.
 */
export const useIdioTracker = (
    clientName: string,
    environment: string = DEFAULT_IDIO_ENVIRONMENT,
): void => {
    useEffect(() => {
        if (document.querySelector('script[src*="/ip.js"]')) {
            return;
        }
        (window as any)._iaq = (window as any)._iaq || [
            ['client', clientName],
            ['track', 'consume'],
        ];
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://s.${environment}/ip.js`;
        document.head.appendChild(script);
    }, [clientName, environment]);
};
